import { Injectable, inject } from '@angular/core';
import { RelationshipsActions } from '@next/core-lib/relationships';
import { UserActions } from '@next/core-lib/user';
import { WindowServiceInjector, WindowService } from '@next/core-lib/window';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { appRoutes } from 'src/app/app.routes';
import * as cycleActions from 'src/app/cycle/store/actions/cycle.actions';

import * as deliveryActions from '../../../delivery/store/actions/delivery.actions';
import * as farmerInsightsActions from '../../../farmer-insights/store/actions/farmer-insights.actions';
import * as feedAdviceActions from '../../../feed-advice/store/feed-advice.actions';
import * as productActions from '../../../product/store/actions/product.actions';
import * as referenceSchemeActions from '../../../reference-scheme/store/reference-scheme.actions';
import * as referenceValueSheetActions from '../../../reference-value-sheet/store/reference-value-sheet.actions';
import * as reportActions from '../../../report/store/actions/report.actions';
import * as settingActions from '../../../settings/store/actions/setting.actions';

const loadSettingsActionsByRoute: Record<string, () => Observable<Action>> = {
  barn: () => of(settingActions.loadBarnsRequest(), settingActions.loadVeterinarianSettings()),
  performancesetting: () =>
    of(
      settingActions.loadObservationSwitch(),
      settingActions.loadRVSSettings(),
      settingActions.loadDayOffset(),
      settingActions.loadUnitOfMeasureSettings(),
    ),
  farmresultsetting: () =>
    of(settingActions.loadBarnsRequest(), settingActions.loadFarmResultSettingsRequest()),
  finalsetupsetting: () => of(settingActions.loadFinalSetupSettings()),
  discountsetting: () => of(settingActions.loadDiscountSettings()),
};

const loadDataActionsByRoute: Record<string, () => Observable<Action>> = {
  cycle: () => of(cycleActions.loadData()),
  delivery: () => of(deliveryActions.loadDeliveriesRequest({})),
  report: () => of(reportActions.loadData()),
  settings: () => EMPTY,
  'farmer-insights': () => of(farmerInsightsActions.loadData()),
  'feed-advice': () => of(cycleActions.loadData(), feedAdviceActions.loadData()),
  product: () => of(productActions.loadData()),
  'reference-scheme': () => of(referenceSchemeActions.loadData()),
  'reference-value-sheet': () => EMPTY,
  'within-cycle-performance': () =>
    of(
      cycleActions.loadData(),
      referenceValueSheetActions.loadRefValSheetData(),
      feedAdviceActions.loadData(),
    ),
};

const loadableRoutes = new Set(
  appRoutes
    .map((route) => route.path)
    .filter(
      (path): path is string =>
        typeof path === 'string' && Object.hasOwn(loadDataActionsByRoute, path),
    ),
);

@Injectable()
export class PoultryEffects {
  private actions$ = inject(Actions);
  private window = inject<WindowService>(WindowServiceInjector);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  locationInitialized$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.locationInitialized),
      switchMap((_) => {
        const parts = this.getPathParts(this.window.getPath());

        if (parts[0] === 'settings') {
          return this.getSettingsLoadActions(parts[1]);
        }

        if (parts.length > 1 && loadableRoutes.has(parts[0])) {
          return this.getLoadDataActions(parts[0]);
        }
        return EMPTY;
      }),
    ),
  );

  relationshipSelectedRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RelationshipsActions.setRelationshipSelectedRedirect),
      switchMap(({ redirectTo }) => {
        const parts = redirectTo ? redirectTo.split('/').slice(1) : [];
        if (parts.length > 1) {
          return of(
            RelationshipsActions.setRelationshipSelectedRedirect({ redirectTo: `/${parts[0]}` }),
          );
        } else {
          return EMPTY;
        }
      }),
    ),
  );

  customerChanged$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.customerChanged),
      switchMap(() => of(farmerInsightsActions.resetFarmerInsightsLocalFilterSettings())),
    ),
  );

  // eslint-disable-next-line: no-any
  getLoadDataActions(urlPart: string) {
    if (urlPart.indexOf('?') !== -1) {
      urlPart = urlPart.split('?')[0];
    }

    const loadActions = loadDataActionsByRoute[urlPart];
    return loadActions ? loadActions() : EMPTY;
  }

  private getSettingsLoadActions(routePart?: string) {
    const normalizedRoutePart = routePart?.split('?')[0] ?? 'finalsetupsetting';
    const loadActions = loadSettingsActionsByRoute[normalizedRoutePart];

    return loadActions ? loadActions() : EMPTY;
  }

  private getPathParts(path: string) {
    return path.replace(/^\//, '').split('/');
  }
}
